import {
  Button,
  Code,
  Divider,
  Group,
  Modal,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useState } from 'react';
import QRCode from 'react-qr-code';
import { ExclamationMark } from 'tabler-icons-react';
import { z } from 'zod';

import { useChangeUserPassword } from 'api/users/changeUserPassword';
import { useDisableMfa } from 'api/users/disableMfa';
import { useEnableEmailMfa } from 'api/users/enableEmailMfa';
import { useSetupTotp } from 'api/users/setupTotp';
import { useVerifyTotp } from 'api/users/verifyTotp';
import { User } from 'types';

type MfaSettingsModalProps = {
  opened: boolean;
  onClose: () => void;
  user: User;
};

const totpVerifySchema = z.object({
  code: z.string().trim().min(1, { message: 'Enter the code' }),
});

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(6, { message: 'Enter your password' }),
    newPassword: z.string().min(6, { message: 'Minimum 6 characters' }),
    confirmPassword: z.string().min(6, { message: 'Confirm your password' }),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

function MfaSettingsModal({ opened, onClose, user }: MfaSettingsModalProps) {
  const enableEmailMfa = useEnableEmailMfa();
  const disableMfa = useDisableMfa();
  const changePassword = useChangeUserPassword();
  const setupTotp = useSetupTotp();
  const verifyTotp = useVerifyTotp();
  const [totpSetup, setTotpSetup] = useState<{
    secret: string;
    otpauthUrl?: string;
  } | null>(null);

  const form = useForm<{ code: string }>({
    initialValues: {
      code: '',
    },
    validate: zodResolver(totpVerifySchema),
  });

  const passwordForm = useForm<{
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: zodResolver(changePasswordSchema),
  });

  const handleEnableEmail = () => {
    enableEmailMfa.mutate(user._id, {
      onSuccess: () => {
        showNotification({
          title: 'MFA enabled',
          message: 'Email verification is now enabled for your account.',
          color: 'teal',
        });
        onClose();
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to enable email MFA.',
          color: 'red',
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleDisable = () => {
    disableMfa.mutate(user._id, {
      onSuccess: () => {
        showNotification({
          title: 'MFA disabled',
          message: 'Multi-factor authentication is now disabled.',
          color: 'teal',
        });
        setTotpSetup(null);
        onClose();
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to disable MFA.',
          color: 'red',
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleSetupTotp = () => {
    setupTotp.mutate(user._id, {
      onSuccess: (data) => {
        setTotpSetup(data);
        form.setValues({ code: '' });
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Failed to start authenticator setup.',
          color: 'red',
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleVerifyTotp = (values: { code: string }) => {
    verifyTotp.mutate(
      { userId: user._id, code: values.code },
      {
        onSuccess: () => {
          showNotification({
            title: 'MFA enabled',
            message: 'Authenticator app is now enabled.',
            color: 'teal',
          });
          setTotpSetup(null);
          onClose();
        },
        onError: () => {
          showNotification({
            title: 'Verification failed',
            message: 'Please check the code and try again.',
            color: 'red',
            icon: <ExclamationMark />,
          });
        },
      }
    );
  };

  const handleChangePassword = (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    changePassword.mutate(
      {
        userId: user._id,
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          showNotification({
            title: 'Password updated',
            message: 'Your password has been changed successfully.',
            color: 'teal',
          });
          passwordForm.reset();
        },
        onError: () => {
          showNotification({
            title: 'Error',
            message: 'Failed to update password. Please try again.',
            color: 'red',
            icon: <ExclamationMark />,
          });
        },
      }
    );
  };

  const isEnabled = Boolean(user.mfaEnabled && user.mfaMethod);

  return (
    <Modal opened={opened} onClose={onClose} title="Multi-factor Authentication">
      <Stack>
        {isEnabled ? (
          <>
            <Text color="gray.7">
              MFA is enabled using
              {user.mfaMethod === 'email'
                ? ' email codes.'
                : ' an authenticator app.'}
            </Text>
            <Group position="right">
              <Button
                color="red"
                variant="outline"
                onClick={handleDisable}
                loading={disableMfa.isLoading}
              >
                Disable MFA
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Text color="gray.7">
              Choose how you want to verify your sign-in.
            </Text>
            <Group spacing={12}>
              <Button
                onClick={handleEnableEmail}
                loading={enableEmailMfa.isLoading}
              >
                Enable Email MFA
              </Button>
              <Button
                variant="outline"
                onClick={handleSetupTotp}
                loading={setupTotp.isLoading}
              >
                Set Up Authenticator App
              </Button>
            </Group>
          </>
        )}

        {totpSetup && (
          <Stack spacing={6}>
            <Text size="sm" color="gray.7">
              Enter this secret in your authenticator app:
            </Text>
            <Code>{totpSetup.secret}</Code>
            {totpSetup.otpauthUrl && (
              <Text size="sm" color="gray.7">
                Scan this QR code with your authenticator app:
              </Text>
            )}
            {totpSetup.otpauthUrl && (
              <Group position="center">
                <div
                  style={{
                    background: '#ffffff',
                    padding: 8,
                    borderRadius: 8,
                  }}
                >
                  <QRCode value={totpSetup.otpauthUrl} size={160} />
                </div>
              </Group>
            )}
            {totpSetup.otpauthUrl && (
              <Text size="xs" color="dimmed">
                Or use this URL:
              </Text>
            )}
            {totpSetup.otpauthUrl && (
              <Text size="xs" color="dimmed">
                {totpSetup.otpauthUrl}
              </Text>
            )}
            <form onSubmit={form.onSubmit(handleVerifyTotp)}>
              <TextInput
                label="Verification code"
                mt={10}
                {...form.getInputProps('code')}
              />
              <Group position="right" mt={10}>
                <Button type="submit" loading={verifyTotp.isLoading}>
                  Verify
                </Button>
              </Group>
            </form>
          </Stack>
        )}

        <Divider my="sm" />
        <Stack spacing={10}>
          <Text weight={600}>Change Password</Text>
          <form onSubmit={passwordForm.onSubmit(handleChangePassword)}>
            <PasswordInput
              label="Current password"
              {...passwordForm.getInputProps('currentPassword')}
            />
            <PasswordInput
              label="New password"
              mt="sm"
              {...passwordForm.getInputProps('newPassword')}
            />
            <PasswordInput
              label="Confirm new password"
              mt="sm"
              {...passwordForm.getInputProps('confirmPassword')}
            />
            <Group position="right" mt="sm">
              <Button type="submit" loading={changePassword.isLoading}>
                Update Password
              </Button>
            </Group>
          </form>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default MfaSettingsModal;
